import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput } from "@components/Forms";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { CHARACTER_LENGTH } from "@constants/index";
import useEscapeKey from "@hooks/useEscapeKey";

const AddGroupModal = ({ isOpen, onClose, onCreateGroup, onUpdateGroup, editMode = false, initialData = null, "data-testid": dataTestId = "add-group-modal" }) => {
    const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
    const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
    const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
    const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
    const fieldErrorTestId = (name) => `${dataTestId}-field-${name}-error`;
    const { t } = useTranslation();
    const { colors } = useTheme();

    const [formData, setFormData] = useState({
        groupProductName: "",
        groupProductDescription: "",
        groupProductColor: "#3B82F6" // Default blue color
    });
    const [errors, setErrors] = useState({});
    const [isUnchanged, setIsUnchanged] = useState(true);

    // Predefined colors
    const predefinedColors = [
        "#3B82F6", // Blue
        "#10B981", // Green
        "#F59E0B", // Yellow
        "#EF4444", // Red
        "#8B5CF6", // Purple
        "#06B6D4", // Cyan
        "#F97316", // Orange
    ];

    // Populate form with initial data in edit mode
    useEffect(() => {
        if (!isOpen) return;
        if (editMode && initialData) {
            setFormData({
                groupProductName: initialData.name || "",
                groupProductDescription: initialData.description || "",
                groupProductColor: initialData.color || "#3B82F6"
            });
            setIsUnchanged(true);
        } else {
            setFormData({
                groupProductName: "",
                groupProductDescription: "",
                groupProductColor: "#3B82F6"
            });
            setIsUnchanged(false);
        }
    }, [editMode, initialData, isOpen]);

    // Check if form data has changed
    useEffect(() => {
        if (editMode && initialData) {
            const hasChanged = 
                formData.groupProductName !== (initialData.name || "") ||
                formData.groupProductDescription !== (initialData.description || "") ||
                formData.groupProductColor !== (initialData.color || "#3B82F6");
            setIsUnchanged(!hasChanged);
        }
    }, [formData, editMode, initialData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleColorSelect = (color) => {
        setFormData(prev => ({
            ...prev,
            groupProductColor: color
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.groupProductName.trim()) {
            newErrors.groupProductName = t("product.group-name-required");
        }

        if (formData.groupProductName.trim().length > CHARACTER_LENGTH.TITLE) {
            newErrors.groupProductName = t("product.group-name-too-long");
        }

        if (formData.groupProductDescription && formData.groupProductDescription.length > CHARACTER_LENGTH.DESCRIPTION) {
            newErrors.groupProductDescription = t("product.description-too-long");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            if (editMode) {
                onUpdateGroup(formData);
            } else {
                onCreateGroup(formData);
            }
            handleClose();
        }
    };

    const handleClose = () => {
        setFormData({
            groupProductName: "",
            groupProductDescription: "",
            groupProductColor: "#3B82F6"
        });
        setErrors({});
        onClose();
    };

    useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start sm:justify-center backdrop-blur-[1px] overflow-x-auto p-4`} data-testid={`${dataTestId}-overlay`}>
            <div className={`${colors.bgModal} max-h-[90vh] w-[500px] min-w-[500px] flex flex-col flex-shrink-0 rounded-lg shadow-xl`} data-testid={dataTestId}>
                {/* Header */}
                <div className={`px-6 py-4 flex-shrink-0`} data-testid={`${dataTestId}-header`}>
                    <div className="flex items-center justify-between">
                        <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
                            {editMode ? (t("product.edit-group")) : (t("product.add-group"))}
                        </h2>
                    </div>
                </div>

                {/* Description */}
                <div className="px-6 pb-4 flex-shrink-0">
                    <p className={`text-sm ${colors.textSecondary}`} data-testid={`${dataTestId}-description`}>
                        {editMode 
                            ? (t("product.edit-group-description")) 
                            : (t("product.add-group-description"))
                        }
                    </p>
                </div>

                {/* Content */}
                <div className="flex-1 px-6 min-h-0" data-testid={`${dataTestId}-content`}>
                    <div className="space-y-4">
                        {/* Group Name */}
                        <div data-testid={fieldContainerTestId("group-name")}>
                            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("group-name")}>
                                {t("product.group-name")} <span className={colors.textRequired}>*</span>
                            </label>
                            <CustomInput
                                type="text"
                                value={formData.groupProductName}
                                onChange={(e) => handleInputChange("groupProductName", e.target.value)}
                                placeholder={t("product.enter-group-name")}
                                maxLength={CHARACTER_LENGTH.TITLE}
                                className={errors.groupProductName ? colors.borderDangerOutLineButton : ""}
                                data-testid={fieldInputTestId("group-name")}
                            />
                            {errors.groupProductName && (
                                <p className={`${colors.textDanger} text-xs mt-1`} data-testid={fieldErrorTestId("group-name")}>{errors.groupProductName}</p>
                            )}
                            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("group-name-length")}>
                                {formData.groupProductName.length}/{CHARACTER_LENGTH.TITLE}
                            </p>
                        </div>

                        {/* Description */}
                        <div data-testid={fieldContainerTestId("description")}>
                            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("description")}>
                                {t("product.description")}
                            </label>
                            <textarea
                                value={formData.groupProductDescription}
                                onChange={(e) => handleInputChange("groupProductDescription", e.target.value)}
                                placeholder={t("product.enter-description")}
                                maxLength={CHARACTER_LENGTH.DESCRIPTION}
                                rows={3}
                                className={`w-full px-3 py-2 border rounded-lg text-sm font-medium focus:outline-none resize-none ${errors.groupProductDescription ? colors.borderDangerOutLineButton : colors.borderPrimary
                                    } ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                                data-testid={fieldInputTestId("description")}
                            />
                            {errors.groupProductDescription && (
                                <p className={`${colors.textDanger} text-xs mt-1`} data-testid={fieldErrorTestId("description")}>{errors.groupProductDescription}</p>
                            )}
                            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("description-length")}>
                                {formData.groupProductDescription.length}/{CHARACTER_LENGTH.DESCRIPTION}
                            </p>
                        </div>

                        {/* Color Selection */}
                        <div data-testid={fieldContainerTestId("color")}>
                            <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`} data-testid={fieldLabelTestId("color")}>
                                {t("product.group-color")}
                            </label>
                            <div className="flex items-center space-x-2">
                                {/* Color Preview */}
                                <div
                                    className={`w-10 h-10 rounded-lg border-2 ${colors.borderColorSwatch}`}
                                    style={{ backgroundColor: formData.groupProductColor }}
                                    data-testid={fieldValueTestId("color-preview")}
                                />

                                {/* Predefined Colors */}
                                <div className="flex space-x-2" data-testid={fieldContainerTestId("color-options")}>
                                    {predefinedColors.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => handleColorSelect(color)}
                                            className={`w-6 h-6 rounded-full border-2 ${formData.groupProductColor === color ? colors.borderColorSwatchSelected : colors.borderColorSwatch
                                                } hover:scale-110 transition-transform`}
                                            style={{ backgroundColor: color }}
                                            title={color}
                                            data-testid={fieldInputTestId(`color-${color.replace("#", "")}`)}
                                        />
                                    ))}

                                    {/* Custom Color Picker */}
                                    <div className="relative">
                                        <input
                                            type="color"
                                            value={formData.groupProductColor}
                                            onChange={(e) => handleColorSelect(e.target.value)}
                                            className={`w-6 h-6 rounded-full border-2 border-dashed ${colors.borderDashedMuted} ${colors.hoverBorderDashedMuted} cursor-pointer opacity-0 absolute`}
                                            title={t("product.custom-color")}
                                            data-testid={fieldInputTestId("color-custom")}
                                        />
                                        <div className={`w-6 h-6 rounded-full border-2 border-dashed ${colors.borderDashedMuted} ${colors.hoverBorderDashedMuted} flex items-center justify-center ${colors.textMuted} ${colors.hoverTextNeutral} pointer-events-none`}>
                                            +
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
                    <div className="flex items-center justify-end space-x-3">
                        <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>
                            {t("common.cancel-button")}
                        </OutlineButton>
                        <PrimaryButton
                            data-testid={`${dataTestId}-confirm-button`}
                            onClick={handleSubmit}
                            disabled={editMode && isUnchanged}
                        >
                            {editMode ? (t("product.update-group")) : (t("product.create-group"))}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddGroupModal;
